/**
 * Pockets: jaw tips, and the throat a ball has to reach (PHY-005).
 *
 * The jaw WALLS are ordinary cushion segments and are handled by the cushion code.
 * Two things are specific to a pocket and live here:
 *
 *   • the **rounded tip** at each end of the mouth — a real jaw is a rubber nose with
 *     a radius, and that radius is what decides whether a ball clipping it is
 *     deflected in or spat back out;
 *   • the **throat** test, which is what "potted" actually means.
 *
 * A ball is potted only once its centre reaches the throat, having got past the jaws
 * first. Testing proximity to the pocket centre instead would pot balls a real table
 * spits out, and would turn `pocketScale` into a difficulty fudge rather than a
 * physical dimension.
 */
import type { PhysicsConfig } from './config.ts';
import type { TableState } from './state.ts';
import type { Pocket, Point } from './table.ts';

/**
 * Earliest time within `maxT` at which ball `i` meets the rounded tip centred at
 * `tip`, or -1. Swept, like every other contact test here.
 */
export function timeToTip(
  state: TableState,
  i: number,
  tip: Point,
  maxT: number,
  contactRadiusM: number,
): number {
  const dx = tip.xM - (state.xM[i] ?? 0);
  const dy = tip.yM - (state.yM[i] ?? 0);
  const vx = state.vxMps[i] ?? 0;
  const vy = state.vyMps[i] ?? 0;

  const a = vx * vx + vy * vy;
  if (a === 0) return -1;

  const b = -(dx * vx + dy * vy);
  if (b >= 0) return -1; // moving away from the tip

  const c = dx * dx + dy * dy - contactRadiusM * contactRadiusM;
  const discriminant = b * b - a * c;
  if (discriminant < 0) return -1;
  if (c <= 0) return 0; // already touching, still closing

  const t = (-b - Math.sqrt(discriminant)) / a;
  return t >= 0 && t <= maxT ? t : -1;
}

/**
 * Bounce ball `i` off a jaw tip. Returns the approach speed, for the event log.
 *
 * Treated as a vertical rubber post rather than a nose above the ball's centre: the
 * tip is the part of the jaw a ball meets side-on, so the height offset that matters
 * at a rail face does not apply. See docs/physics.md deviation D6.
 */
export function resolveTipContact(
  state: TableState,
  i: number,
  tip: Point,
  config: PhysicsConfig,
): number {
  let nx = (state.xM[i] ?? 0) - tip.xM;
  let ny = (state.yM[i] ?? 0) - tip.yM;
  const distance = Math.sqrt(nx * nx + ny * ny);
  if (distance === 0) return 0;
  nx /= distance;
  ny /= distance;

  const vx = state.vxMps[i] ?? 0;
  const vy = state.vyMps[i] ?? 0;
  const un = vx * nx + vy * ny;
  if (un >= 0) return 0; // already leaving

  const approachSpeed = -un;

  // Jaws are the same rubber as the rails, so they lose speed the same way.
  const speedFraction = approachSpeed / config.cushionRestitutionSpeedMps;
  const blend = speedFraction > 1 ? 1 : speedFraction;
  const restitution =
    config.cushionRestitutionLow +
    (config.cushionRestitutionHigh - config.cushionRestitutionLow) * blend;

  const jn = (1 + restitution) * approachSpeed;
  state.vxMps[i] = vx + jn * nx;
  state.vyMps[i] = vy + jn * ny;

  return approachSpeed;
}

/**
 * The pocket ball `i` has dropped into, or -1.
 *
 * Two conditions: the centre is within the throat, AND it has crossed the mouth line.
 * For a corner the second is implied by the first (the throat circle only touches the
 * mouth line). A side pocket's throat is centred ON its mouth line, so without the
 * second test half of it lay on the cloth: a ball rolling along the rail, or aimed well
 * past a jaw, dropped before it reached the pocket at all (PHY-009).
 */
export function pocketReached(state: TableState, i: number, pockets: readonly Pocket[]): number {
  const x = state.xM[i] ?? 0;
  const y = state.yM[i] ?? 0;

  for (let p = 0; p < pockets.length; p++) {
    const pocket = pockets[p];
    if (pocket === undefined) continue;
    const dx = x - pocket.centre.xM;
    const dy = y - pocket.centre.yM;
    if (dx * dx + dy * dy > pocket.throatRadiusM * pocket.throatRadiusM) continue;
    const past =
      (x - pocket.mouthCentre.xM) * pocket.outward.xM +
      (y - pocket.mouthCentre.yM) * pocket.outward.yM;
    if (past > 0) return p;
  }
  return -1;
}

/** Take a ball out of play. A potted ball stops dead; nothing else touches it. */
export function pot(state: TableState, i: number): void {
  state.potted[i] = 1;
  state.vxMps[i] = 0;
  state.vyMps[i] = 0;
  state.wxRadS[i] = 0;
  state.wyRadS[i] = 0;
  state.wzRadS[i] = 0;
}
